import clsx from "clsx";
import { useCallback, useEffect, useRef, useState, RefObject } from "react";
import axios from "axios";
import Bowser from "bowser";
import { AnimatePresence, motion } from "framer-motion";
import { AnimateChangeInHeight } from "@/components/animations/animateHeight";
import { CheckIcon, DevicePhoneMobileIcon } from "@heroicons/react/20/solid";
import { useMutation } from "@tanstack/react-query";
import { getCurrentTime, useSearchEngineDetector } from "@/lib/utils";
import {
	employment_status,
	loan_type,
	living_status,
	VITE_ZEM_GATEWAY_URL,
	LOAN_TYPE_BUSINESS,
	LOAN_TYPE_BUSINESS_ALL,
	EMP_STAT_SELF_EMPLOYED,
	EMP_STAT_SELF_EMPLOYED_ID,
	LOAN_PURPOSE_BUSINESS,
	LOAN_TYPE_BUSINESS_COMMERCIAL_PROPERTY,
	LOAN_TYPE_BUSINESS_WORKING_CAPITAL,
	LOAN_TYPE_BUSINESS_TRUCK,
	LOAN_TYPE_BUSINESS_EQUIPMENT,
} from "@/lib/constants";
import {
	PersonalDetails,
	PhoneValidation,
	LoanDetails,
	AdditionalInfo,
	BulletSteps,
} from "@/components/forms/parts/index";
import { useFormDataStore, useFormStateStore } from "@/components/forms/context";

async function postZap<T>(data: T) {
	try {
		const response = await fetch(`${VITE_ZEM_GATEWAY_URL}/api/send-data`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ data }),
		});
		if (!response.ok) {
			const errorText = await response.text();
			console.error(`Error in sending data to the server. Status: `, response.status);
			console.error("Response body:", errorText);
			return;
		}
		const responseData = await response.json();
		return responseData;
	} catch (error) {
		console.error(`Network error while sending data to the server: `, error);
	}
}

async function sendEmail<T>(data: T) {
	try {
		const response = await fetch(`${VITE_ZEM_GATEWAY_URL}/api/send-email`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ data }),
		});
		if (!response.ok) {
			const errorText = await response.text();
			console.error(`Error in sending email data to the server. Status: `, response.status);
			console.error("Response body:", errorText);
			return;
		}
		const responseData = await response.json();
		return responseData;
	} catch (error) {
		console.error(`Network error while sending email: `, error);
	}
}

const steps = [
	{
		id: "Step 1",
		name: "Personal Details",
	},
	{
		id: "Step 2",
		name: "Verification",
	},
	{
		id: "Step 3",
		name: "Loan Details",
	},
	{
		id: "Step 4",
		name: "Additional Information",
	},
	{
		id: "Success Page",
		name: "Completed",
	},
];

function usePrevious(value: number) {
	let ref = useRef(value);

	useEffect(() => {
		ref.current = value;
	}, [value]);

	return ref.current;
}

const variants = {
	initial: (isForwards: boolean) => {
		return {
			opacity: 0,
		};
	},
	animate: {
		opacity: 1,
	},
	exit: (isForwards: boolean) => {
		return {
			opacity: 0,
		};
	},
};

type QuickFormProps = {
	className?: string;
	loanTypes?: string;
	siteTitle?: string;
	rootElement?: HTMLElement;
};

export function QuickFormMain({ className, loanTypes, siteTitle, rootElement }: QuickFormProps) {
	//Get values and setters from Zustand store
	const { formValues, setFormValue, resetFormValues } = useFormDataStore();
	const {
		applicationType,
		firstName,
		lastName,
		email,
		state,
		loanAmount,
		phone,
		loanPurpose,
		loanType,
		assetType,
		purchaseStage,
		assetAge,
		purchaseFrom,
		livingStatus,
		employmentStatus,
		abnStatus,
		gstStatus,
		combinedIncome,
		creditHistory,
		runningFranchise,
	} = formValues;

	const {
		setLoanAmountMin,
		selectedIndex,
		setSelectedIndex,
		isPhoneVerified,
		setPhoneVerified,
		enabledFields,
		setEnabledFields,
		enabledLoans,
		setEnabledLoans,
		isSubmitted,
		setIsSubmitted,
		validateFormDetails,
		appSettings,
		setAppSettings,
		formContainer,
		setFormContainer,
		toggleTC, //Enables proceeding to the next page if T&A has been agreed.
		setToggleTC,
	} = useFormStateStore();

	/* Partner Info */
	const [isPartner, setIsPartner] = useState("No");
	const [partnerName, setPartnerName] = useState("");
	const [partnerId, setPartnerId] = useState("");
	const [partnerEmail, setPartnerEmail] = useState("");
	const [partnerWebsite, setPartnerWebsite] = useState("");
	const [partnerAssetType, setPartnerAssetType] = useState("");
	const hasFetched = useRef(false);

	const detectedSearchEngine = useSearchEngineDetector();
	const [searchEngine, setSearchEngine] = useState<string>("Direct");
	const [isNavDivSmall, setIsNavDivSmall] = useState(false);
	const [tabHasFocus, setTabHasFocus] = useState(true);
	const [browserInfo, setBrowserInfo] = useState({});
	const timer = useRef<NodeJS.Timeout | undefined>(undefined);
	const [userIP, setUserIP] = useState("");
	const [usingVPN, setUsingVPN] = useState(false);
	const prevIndex = usePrevious(selectedIndex);
	const isForwards = prevIndex === undefined ? true : selectedIndex > prevIndex;
	const sourceUrl = window.location.href;

	const [assetTypeID, setAssetTypeID] = useState(0);
	const loanTypesArray = LOAN_TYPE_BUSINESS_ALL.split(",").map((t) => t.trim()); // Array of all business loan types
	const loanTypesObject = loan_type.find((type) => type.option === loanType); // Array of all business loan types
	const residentialTypeObject = living_status.find((type) => type.value === livingStatus);
	const residential_status_id = residentialTypeObject ? residentialTypeObject.id : null;
	const employmentTypeObject = employment_status.find((type) => type.value === employmentStatus);
	const employment_status_id = employmentTypeObject ? employmentTypeObject.id : null;

	const navDivRef = useRef<HTMLDivElement | null>(null); //References to the Navigation Button's Div Container
	const didLoanAmountSync = useRef<boolean>(false);

	// Apply Loan Type
	useEffect(() => {
		if (selectedIndex == 0) {
			const loans = loanTypes?.split(",").map((t) => t.trim()) ?? loanTypesArray;

			if (loans.length === 1) {
				setFormValue("loanType", loans[0]);
				setEnabledFields(loans[0]);
			}
			setFormValue("loanPurpose", LOAN_PURPOSE_BUSINESS);
			setFormValue("applicationType", "2"); // 1 - Consumer / 2 - Commercial
			setEnabledLoans(loans); // Sets the allowed Loan Type options.
		}
	}, [selectedIndex, setFormValue]);

	// Dynamically set the asset_type_id value
	useEffect(() => {
		if (loanTypesArray.includes(loanType)) {
			const asset_type_id = loanTypesObject ? loanTypesObject.id : 0;
			setAssetTypeID(Number(asset_type_id));
		}
	}, [loanType, loanTypesArray, loanTypesObject]);

	// Apply Color Theme Settings 1
	useEffect(() => {
		setAppSettings(siteTitle ?? "Business Finance");
	}, [siteTitle, setAppSettings]);

	// Apply Color Theme Settings 2
	useEffect(() => {
		if (!rootElement) return;

		rootElement?.style.setProperty(`--bF-theme-color`, appSettings.themeColor);
		rootElement?.style.setProperty(`--bF-theme-h-text`, appSettings.themeHeaderTxt);
		rootElement?.style.setProperty(`--bF-hover-color`, appSettings.hoverColor);
		rootElement?.style.setProperty(`--bF-bullet-bg`, appSettings.bulletBG);
	}, [appSettings, rootElement]);

	// For Navigation Buttons (Previous, Next, Submit)
	useEffect(() => {
		const container = navDivRef.current;
		if (!container) return;

		const observer = new ResizeObserver(([entry]) => {
			let limiter = 600;

			setFormContainer(entry.contentRect.width);
			setIsNavDivSmall(entry.contentRect.width < limiter);
		});

		observer.observe(container);

		return () => observer.disconnect();
	}, [selectedIndex, formContainer]);

	const scrollToHeading = () => {
		if (!rootElement) return;

		if (window.matchMedia("(max-width: 767px)").matches) {
			setTimeout(() => {
				const additionalOffset = 0; // adjust if you want some spacing above the form
				const targetPosition =
					rootElement.getBoundingClientRect().top + window.scrollY - additionalOffset;

				window.scrollTo({
					top: targetPosition,
					behavior: "smooth",
				});
			}, 100);
		}
	};

	async function getUserIP() {
		const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/getIP`);
		const data = await response.data;
		setUserIP(data.ip);
		setUsingVPN(data.vpn);
	}

	useEffect(() => {
		setSearchEngine(detectedSearchEngine);
	}, [detectedSearchEngine]);

	// Detect what browser and user IP
	useEffect(() => {
		const browser = Bowser.parse(window.navigator.userAgent);
		const info = `${browser.browser.name} ${browser.browser.version}, ${browser.os.name}, ${browser.platform.type}`;
		setBrowserInfo(info);
	}, []);

	window.leadGenerated = window.leadGenerated || false;

	const loadDataLayerScript = () => {
		if (!window.leadGenerated) {
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: "generate_lead",
				enhanced_conversion_data: {
					email: email,
					phone_number: "+61" + phone?.substring(1),
					first_name: firstName,
					last_name: lastName,
				},
			});
			window.leadGenerated = true;
		}
	};

	const IP_STORAGE_KEY = "processedIPs";
	const EXPIRATION_TIME = 30 * 60 * 1000;

	const getProcessedIPs = (): { userIP: string; timestamp: number }[] => {
		const ips = localStorage.getItem(IP_STORAGE_KEY);
		return ips ? JSON.parse(ips) : [];
	};

	const addProcessedIP = (userIP: string) => {
		const ips = getProcessedIPs();
		const now = Date.now();
		const updatedIPs = ips.filter(({ timestamp }) => now - timestamp < EXPIRATION_TIME);
		updatedIPs.push({ userIP, timestamp: now });
		localStorage.setItem(IP_STORAGE_KEY, JSON.stringify(updatedIPs));
	};

	const isIPProcessed = (userIP: string): boolean => {
		const ips = getProcessedIPs();
		const now = Date.now();
		return ips.some(
			({ userIP: storedIP, timestamp }) =>
				storedIP === userIP && now - timestamp < EXPIRATION_TIME
		);
	};

	const getFilteredFormValues = (formValues: any, loanType: string) => {
		// Remove form values that are not applicable to a Loan Type
		if (loanType === LOAN_TYPE_BUSINESS_WORKING_CAPITAL) {
			const {
				assetCost,
				deposit,
				assetType,
				propertyCategory,
				propertyOption,
				// propertyLocation,
				propertyAddress,
				rentalIncome,
				propertyValuation,
				assessedValue,
				assetAge,
				otherAssetType,
				purchaseStage,
				purchaseFrom,
				financeReason,
				otherFinanceReason,
				abnStatus,
				gstStatus,
				combinedIncome,
				...rest
			} = formValues;
			return rest;
		} else if (loanType === LOAN_TYPE_BUSINESS_COMMERCIAL_PROPERTY) {
			const {
				businessAbnStatus,
				businessName,
				abnYears,
				abnMonths,
				trusteeName,
				abn,
				runningFranchise,
				franchiseBrand,
				assetAge,
				otherAssetType,
				purchaseStage,
				purchaseFrom,
				financeReason,
				otherFinanceReason,
				...rest
			} = formValues;
			return rest;
		} else {
			const {
				assetCost,
				deposit,
				assetType,
				otherAssetType,
				financeReason,
				otherFinanceReason,
				propertyCategory,
				propertyOption,
				// propertyLocation,
				propertyAddress,
				rentalIncome,
				propertyValuation,
				assessedValue,
				businessAbnStatus,
				businessName,
				abnYears,
				abnMonths,
				trusteeName,
				abn,
				runningFranchise,
				franchiseBrand,
				...rest
			} = formValues;

			if (loanType === LOAN_TYPE_BUSINESS_EQUIPMENT) {
				return {
					...rest,
					assetType,
					otherAssetType,
					financeReason,
					otherFinanceReason,
				};
			} else if (loanType === LOAN_TYPE_BUSINESS_TRUCK) {
				return { ...rest, assetType, otherAssetType };
			}
			// Business Vehicle
			return rest;
		}
	};

	const sendPreZap = useMutation({
		mutationKey: [formValues],
		mutationFn: async () => {
			const filteredFormValues = getFilteredFormValues(formValues, loanType);

			const zapPost = {
				...filteredFormValues,
				dateSubmitted: getCurrentTime(),
				application_type_id: Number(applicationType),
				siteTitle: `${siteTitle}`,
				formName: `${siteTitle} Partial Submission`,
				firstName: firstName,
				lastName: lastName,
				email: email,
				phone: phone,
				state: state,
				loanType: loanType,
				loanAmount: loanAmount,
				loanPurpose: loanPurpose,
				assetType: assetType,
				asset_type_id: assetTypeID,
				assetAge: assetAge,
				purchaseStage: purchaseStage,
				purchaseFrom: purchaseFrom,
				runningFranchise:
					loanType === LOAN_TYPE_BUSINESS_COMMERCIAL_PROPERTY ? runningFranchise : "",
				livingStatus: livingStatus,
				residential_status_id: residential_status_id,
				employmentStatus: EMP_STAT_SELF_EMPLOYED,
				employment_status_id: EMP_STAT_SELF_EMPLOYED_ID,
				abnStatus: abnStatus,
				gstStatus: gstStatus,
				combinedIncome: combinedIncome,
				creditHistory: creditHistory,
				isPartial: "Yes",
				source_id: 18,
				source: sourceUrl,
				isUsingVPN: usingVPN.toString(),
				userIP: userIP,
				userAgent: browserInfo,
				searchEngine: searchEngine,
				isReferrer: isPartner,
				partnerName: partnerName,
				partnerEmail: partnerEmail,
				referralId: partnerId,
				partnerWebsite: partnerWebsite,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) =>
						value !== applicationType &&
						value !== null &&
						value !== undefined &&
						value !== ""
				)
			);

			const data = await postZap(cleanedZapPost);
			return data;
		},
		onSuccess() {},
	});

	const sendPreZapMail = useMutation({
		mutationKey: [formValues],
		mutationFn: async () => {
			const filteredFormValues = getFilteredFormValues(formValues, loanType);

			const zapPost = {
				...filteredFormValues,
				dateSubmitted: getCurrentTime(),
				application_type_id: Number(applicationType),
				siteTitle: `${siteTitle}`,
				formName: `${siteTitle} Partial Submission`,
				firstName: firstName,
				lastName: lastName,
				email: email,
				phone: phone,
				state: state,
				loanType: loanType,
				loanAmount: loanAmount,
				loanPurpose: loanPurpose,
				assetType: assetType,
				asset_type_id: assetTypeID,
				assetAge: assetAge,
				purchaseStage: purchaseStage,
				purchaseFrom: purchaseFrom,
				livingStatus: livingStatus,
				residential_status_id: residential_status_id,
				employmentStatus: EMP_STAT_SELF_EMPLOYED,
				employment_status_id: EMP_STAT_SELF_EMPLOYED_ID,
				abnStatus: abnStatus,
				gstStatus: gstStatus,
				combinedIncome: combinedIncome,
				creditHistory: creditHistory,
				isPartial: "Yes",
				source_id: 18,
				source: sourceUrl,
				isUsingVPN: usingVPN.toString(),
				userIP: userIP,
				userAgent: browserInfo,
				searchEngine: searchEngine,
				isReferrer: isPartner,
				partnerName: partnerName,
				partnerEmail: partnerEmail,
				referralId: partnerId,
				partnerWebsite: partnerWebsite,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) =>
						value !== applicationType &&
						value !== null &&
						value !== undefined &&
						value !== ""
				)
			);

			const data = await sendEmail(cleanedZapPost);
			return data;
		},
		onSuccess() {},
	});

	const sendZap = useMutation({
		mutationKey: [formValues],
		mutationFn: async () => {
			const filteredFormValues = getFilteredFormValues(formValues, loanType);

			const zapPost = {
				...filteredFormValues,
				dateSubmitted: getCurrentTime(),
				application_type_id: Number(applicationType),
				siteTitle: `${siteTitle}`,
				formName: `${siteTitle} Quick Quote`,
				loanAmount: loanAmount,
				combinedIncome: combinedIncome,
				asset_type_id: assetTypeID,
				residential_status_id: residential_status_id,
				employmentStatus: EMP_STAT_SELF_EMPLOYED,
				employment_status_id: EMP_STAT_SELF_EMPLOYED_ID,
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				searchEngine: searchEngine,
				isReferrer: isPartner,
				partnerName: partnerName,
				partnerEmail: partnerEmail,
				referralId: partnerId,
				partnerWebsite: partnerWebsite,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) =>
						value !== applicationType &&
						value !== null &&
						value !== undefined &&
						value !== ""
				)
			);

			const data = await postZap(cleanedZapPost);
			return data;
		},
		onSuccess() {
			resetFormValues();
			setToggleTC(false);
		},
	});

	const sendMail = useMutation({
		mutationKey: [formValues],
		mutationFn: async () => {
			const filteredFormValues = getFilteredFormValues(formValues, loanType);

			const zapPost = {
				...filteredFormValues,
				dateSubmitted: getCurrentTime(),
				application_type_id: Number(applicationType),
				siteTitle: `${siteTitle}`,
				formName: `${siteTitle} Quick Quote`,
				loanAmount: loanAmount,
				combinedIncome: combinedIncome,
				asset_type_id: assetTypeID,
				residential_status_id: residential_status_id,
				employmentStatus: EMP_STAT_SELF_EMPLOYED,
				employment_status_id: EMP_STAT_SELF_EMPLOYED_ID,
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				searchEngine: searchEngine,
				isReferrer: isPartner,
				partnerName: partnerName,
				partnerEmail: partnerEmail,
				referralId: partnerId,
				partnerWebsite: partnerWebsite,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) =>
						value !== applicationType &&
						value !== null &&
						value !== undefined &&
						value !== ""
				)
			);

			const data = await sendEmail(cleanedZapPost);
			return data;
		},
		onSuccess() {
			setToggleTC(false);
		},
	});

	// Prevents Next button from being spammed causing skipping multiple pages.
	const [isPageLoading, setIsPageLoading] = useState(false);

	async function next() {
		if (isPageLoading) return;

		if ([0, 2, 3].includes(selectedIndex)) {
			let validate = validateFormDetails;
			const isValid = validate?.();
			if (!isValid) return; // Stops next page if form errors exist.
		}
		setSelectedIndex(selectedIndex + 1);

		if (selectedIndex < steps.length - 1) {
			setIsPageLoading(true);
			scrollToHeading();
			setSelectedIndex(selectedIndex + 1);
			setTimeout(() => setIsPageLoading(false), 500); // Add delay to give time for components to load.
		}
	}

	function prev() {
		if (selectedIndex === 1) {
			scrollToHeading();
			setSelectedIndex(selectedIndex - 1);
			// For enabled multiple loan purpose, reset Loan Purpose and Asset Type in case loan type changes.
			if (enabledLoans.length > 1 && formValues.loanType !== LOAN_TYPE_BUSINESS) {
				setFormValue("loanPurpose", "");
				setFormValue("assetType", "");
			}
		} else if (selectedIndex > 0 && selectedIndex < steps.length - 1) {
			scrollToHeading();
			setSelectedIndex(selectedIndex - 1);
		}
	}

	function close() {
		setSelectedIndex(0);

		const $ = (window as any).jQuery;
		if (!siteTitle?.includes("Compare")) {
			if ($) {
				const bodyClass = document.body.className;

				if (bodyClass.includes("home")) {
					$("#customModalBackdrop").css("display", "none");
					// 86cz95vka
					$("body").removeClass("no-scroll");
					$(".quick-quote-loan-amount").val(30000);
				}
			}
		} else {
			if ($) {
				const bodyClass = document.body.className;

				if (bodyClass.includes("home")) {
					$(".jade-fullscreen-quote").css("display", "none");
				}
			}
		}
	}

	// Handle External Modal Close Button (XCircle)
	useEffect(() => {
		const handleModalClose = () => {
			// Only reset when on the success page
			if (selectedIndex === steps.length - 1) {
				setIsSubmitted(false);
				setPhoneVerified(false);
				resetFormValues();
				setEnabledFields("");
				setSelectedIndex(0);
				setToggleTC(false);
				close();
			}
		};

		// Find the external close button
		const closeButton = document.querySelector("#modalCloseBtn, #customModalClose");
		if (closeButton) {
			closeButton.addEventListener("click", handleModalClose);
		}

		// Cleanup On Unmount
		return () => {
			if (closeButton) {
				closeButton.removeEventListener("click", handleModalClose);
			}
		};
	}, [selectedIndex, isSubmitted]);

	// Detect if tab is inactive
	useEffect(() => {
		const handleFocus = () => {
			setTabHasFocus(true);
			if (timer.current !== undefined) {
				clearTimeout(timer.current);
			}
		};

		const handleBlur = () => {
			setTabHasFocus(false);
			timer.current = setTimeout(() => {
				// Handle the tab being inactive for 3 minutes here
				if (isPhoneVerified && !isSubmitted && !isIPProcessed(userIP)) {
					sendPreZap.mutate();
					sendPreZapMail.mutate();
					addProcessedIP(userIP);
				}
			}, 180000); // 180000 milliseconds = 3 minutes
		};

		window.addEventListener("focus", handleFocus);
		window.addEventListener("blur", handleBlur);

		// Clean up
		return () => {
			window.removeEventListener("focus", handleFocus);
			window.removeEventListener("blur", handleBlur);
			if (timer.current !== undefined) {
				clearTimeout(timer.current);
			}
		};
	}, [isPhoneVerified, isSubmitted, userIP]);

	// Detect when tab is reloaded and closed
	useEffect(() => {
		if (selectedIndex !== 4) {
			const handleBeforeUnload = (event: any) => {
				if (isPhoneVerified && !isSubmitted && !isIPProcessed(userIP)) {
					sendPreZap.mutate();
					sendPreZapMail.mutate();
					addProcessedIP(userIP);
				}
				setPhoneVerified(false);
				setIsSubmitted(false);
				setSelectedIndex(0);
				resetFormValues();
			};

			window.addEventListener("beforeunload", handleBeforeUnload);
			window.addEventListener("pagehide", handleBeforeUnload);

			return () => {
				window.removeEventListener("beforeunload", handleBeforeUnload);
				window.removeEventListener("pagehide", handleBeforeUnload);
			};
		}
	}, [isPhoneVerified, isSubmitted, selectedIndex, userIP]);

	// Go to next page when phone is verified
	useEffect(() => {
		if (selectedIndex === 4 && isPhoneVerified && !isSubmitted) {
			sendMail.mutate();
			sendZap.mutate();
			setIsSubmitted(true);
			loadDataLayerScript();
		}
		if (isSubmitted) {
			setIsSubmitted(false);
			setPhoneVerified(false);
			resetFormValues();
			setEnabledFields("");
		}
	}, [isPhoneVerified, selectedIndex, isSubmitted]);

	// Go to next page when phone is verified
	useEffect(() => {
		if (isPhoneVerified) {
			next();
			getUserIP();
		}
	}, [isPhoneVerified]);

	const handleKeyDown = useCallback((event: any) => {
		if (event.key === "Enter") {
			event.preventDefault();
		}
	}, []);

	// PURPOSE: JQuery for getting the loan amount value field from the page to the form
	// Jade Truck have different page id for why jade page
	// Jade Finance and Jade Boat Loans why jade do not have the input field
	useEffect(() => {
		const $ = (window as any).jQuery;

		if ($) {
			const bodyClass = document.body.classList;
			const isModalQF = bodyClass.contains("home") || bodyClass.contains("page-template");
			const hasCalculator = $("#bizPropertyCalctrigger, #bizCalctrigger").length > 0;

			if (isModalQF || hasCalculator) {
				const updateLoanAmount = (amountID: string) => {
					const inputValue = $(amountID).val() as string;

					if (inputValue) {
						let formattedValue = +inputValue.replace(/[^0-9]/g, "");
						setFormValue("loanAmount", formattedValue.toLocaleString());
					}
				};

				// 86czx4bdc - Read calculator's loan amount once per mount (when modal + form become visible)
				/*
				if (!didLoanAmountSync.current) {
					updateLoanAmount();
					didLoanAmountSync.current = true;
				}
        */

				// Trigger Update Loan Amount on CTA click
				$(document).on("click", "div.quick-quote-go", function () {
					updateLoanAmount("#amount_dt.quick-quote-loan-amount");
				});
				$(document).on("click", "div.btn-holdr a.btn-solid", function (this: HTMLElement) {
					if ($(this).text().trim() === "Get Free Quote") {
						updateLoanAmount("#amount[placeholder='Loan Amount']");
					}
				});
				// $("#amount_dt.quick-quote-loan-amount").on("change", () =>
				// 	updateLoanAmount("#amount_dt.quick-quote-loan-amount")
				// );

				if (hasCalculator) {
					$(document).on(
						"click",
						"#bizFcalc-trigger-cta, #bizFcalc-trigger-cta-mobile",
						function () {
							updateLoanAmount("#bizFcalc-loan-amount");
						}
					);
				}
			}

			return () => {
				if (isModalQF) {
					$(document).off("click", "div.quick-quote-go");
					$("#amount_dt.quick-quote-loan-amount").off("change");
				}
				if (hasCalculator) {
					$(document).off("click", "#bizFcalc-trigger-cta");
					$(document).off("click", "#bizFcalc-trigger-cta-mobile");
				}
			};
		}
	}, [setFormValue]);

	return (
		<div ref={navDivRef} className={clsx(className, "bizF-bg-white")}>
			{sendZap.isPending && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.2 }}
					className="bizF-w-full bizF-h-full bizF-fixed bizF-top-0 bizF-left-0 bizF-bg-slate-200 bizF-bg-opacity-75 bizF-z-50"
				>
					<div className="bizF-flex bizF-justify-center bizF-items-center bizF-h-full bizF-w-full">
						<div role="status">
							<svg
								aria-hidden="true"
								className="bizF-w-12 bizF-h-12 bizF-text-opacity-0 bizF-text-white bizF-animate-spin bizF-bg-opacity-0 bizF-fill-[--bF-theme-color]"
								viewBox="0 0 100 101"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
									fill="currentColor"
								/>
								<path
									d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
									fill="currentFill"
								/>
							</svg>
							<span className="bizF-sr-only">Loading...</span>
						</div>
					</div>
				</motion.div>
			)}

			{/* Form */}
			<AnimateChangeInHeight>
				<form
					className="bizF-pb-3 bizF-pt-8 md:bizF-pt-4 bizF-px-4"
					onKeyDown={handleKeyDown}
					onSubmit={(e) => e.preventDefault()}
				>
					<AnimatePresence initial={false} mode="wait" custom={isForwards}>
						{selectedIndex === 0 && (
							<motion.div
								key={0}
								initial="initial"
								animate="animate"
								exit="exit"
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								variants={variants}
								custom={isForwards}
								className="bizF-space-y-4"
							>
								<FormHeading />
								<PersonalDetails loanType={loanType} />
							</motion.div>
						)}

						{selectedIndex === 1 && (
							<motion.div
								key={1}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
							>
								{!isPhoneVerified ? (
									<>
										<OTPHeading />
										<PhoneValidation />
									</>
								) : (
									<>
										<FormVerified />
									</>
								)}
							</motion.div>
						)}

						{selectedIndex === 2 && (
							<motion.div
								key={2}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="bizF-space-y-4"
							>
								<FormHeading />
								<LoanDetails />
							</motion.div>
						)}

						{selectedIndex === 3 && (
							<motion.div
								key={3}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="bizF-space-y-4"
							>
								<FormHeading />
								<AdditionalInfo />
							</motion.div>
						)}

						{selectedIndex === 4 && (
							<motion.div
								key={4}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="space-y-4"
							>
								<FormSuccessHeading />
							</motion.div>
						)}
					</AnimatePresence>
				</form>
			</AnimateChangeInHeight>

			{/* Navigation */}
			{selectedIndex !== 4 ? (
				<div
					className={clsx(
						isNavDivSmall ? "bizF-flex-wrap" : "bizF-flex-nowrap",
						"bizF-flex bizF-items-center bizF-justify-center bizF-border-t bizF-border-gray-200 bizF-bg-white bizF-mt-2 bizF-py-3 bizF-px-4"
					)}
				>
					<BulletSteps
						steps={steps}
						selectedIndex={selectedIndex}
						navRef={isNavDivSmall}
					/>
					<div
						className={clsx(
							selectedIndex === 4
								? "bizF-hidden"
								: "bizF-flex bizF-flex-1 bizF-justify-end"
						)}
					>
						<button
							disabled={selectedIndex === 1 && !isPhoneVerified}
							className={clsx(
								selectedIndex === 0 || (selectedIndex === 1 && !isPhoneVerified)
									? "bizF-hidden"
									: isNavDivSmall
									? "bizF-w-full"
									: "bizF-w-auto",
								"bizF-bg-white hover:bizF-bg-gray-50 bizF-relative bizF-inline-flex bizF-items-center bizF-rounded-md bizF-justify-center bizF-px-3 bizF-py-2 bizF-text-md bizF-font-semibold bizF-text-gray-900 bizF-ring-1 bizF-ring-inset bizF-ring-gray-300 focus-visible:bizF-outline-offset-0"
							)}
							onClick={() => prev()}
						>
							Previous
						</button>
						<button
							className={clsx(
								(selectedIndex === 0 && !toggleTC) ||
									(selectedIndex === 1 && !isPhoneVerified)
									? "bizF-bg-gray-100 bizF-text-gray-500 bizF-ring-gray-100"
									: isNavDivSmall
									? "bizF-bg-[--bF-theme-color] hover:bizF-bg-[--bF-hover-color] bizF-text-white bizF-w-full"
									: "bizF-bg-[--bF-theme-color] hover:bizF-bg-[--bF-hover-color] bizF-text-white bizF-w-auto",
								"bizF-relative bizF-ml-3 bizF-inline-flex bizF-items-center bizF-rounded-md bizF-justify-center bizF-px-3 bizF-py-2 bizF-text-md bizF-font-semibold bizF-ring-1 bizF-ring-inset bizF-ring-[--bF-theme-color] focus-visible:bizF-outline-offset-0"
							)}
							onClick={() => {
								if (selectedIndex !== 1 || isPhoneVerified) {
									next();
								}
							}}
						>
							{selectedIndex === 3 ? "Submit" : "Next"}
						</button>
					</div>
				</div>
			) : (
				<div className="bizF-flex bizF-items-center bizF-flex-wrap bizF-justify-center bizF-border-t bizF-border-gray-200 bizF-bg-white bizF-mt-2 bizF-px-4 bizF-py-3 sm:bizF-px-6 sm:bizF-justify-between">
					<button
						className="bizF-bg-[--bF-theme-color] hover:bizF-bg-[--bF-hover-color] bizF-relative bizF-mb-2 bizF-mx-auto bizF-block bizF-w-1/3 bizF-text-center bizF-rounded-md bizF-px-3 bizF-py-2 text-md bizF-font-semibold bizF-text-white bizF-ring-1 bizF-ring-inset bizF-ring-[--bF-theme-color] focus-visible:bizF-outline-offset-0"
						onClick={() => {
							close();
						}}
					>
						Close
					</button>
				</div>
			)}
		</div>
	);
}

function OTPHeading({ className }: { className?: string }) {
	return (
		<>
			<div className={clsx(className, "bizF-relative")}>
				<div className="bizF-mx-auto bizF-flex bizF-h-20 bizF-w-20 bizF-items-center bizF-justify-center bizF-rounded-full bizF-bg-[--bF-bullet-bg]">
					<DevicePhoneMobileIcon
						className="bizF-h-10 bizF-w-10 bizF-text-[--bF-theme-color]"
						aria-hidden="true"
					/>
				</div>
				<div className="bizF-mt-3 bizF-text-center sm:bizF-mt-5">
					<h3 className="bizF-text-xl bizF-font-semibold bizF-leading-8 bizF-text-gray-900">
						Verify Your Number
					</h3>
				</div>
			</div>
		</>
	);
}

function FormHeading({ className }: { className?: string }) {
	return (
		<>
			<div className={clsx(className, "bizF-relative")}>
				<div className=" bizF-text-center">
					<h2 className="bizF-text-[2.2rem] bizF-mb-3 bizF-font-semibold bizF-leading-8 bizF-text-[--bF-theme-h-text]">
						Get a Quick Quote
					</h2>
					<div className="bizF-mt-2">
						<p className="bizF-text-md bizF-text-gray-500">
							This will not affect your credit score
						</p>
					</div>
				</div>
			</div>
		</>
	);
}

function FormSuccessHeading({ className }: { className?: string }) {
	return (
		<>
			<div className={clsx(className, "bizF-relative")}>
				<div className="bizF-mx-auto bizF-flex bizF-h-20 bizF-w-20 bizF-items-center bizF-justify-center bizF-rounded-full bizF-bg-[--bF-bullet-bg]">
					<CheckIcon
						className="bizF-h-10 bizF-w-10 bizF-text-[--bF-theme-color]"
						aria-hidden="true"
					/>
				</div>
				<div className="bizF-mt-3 bizF-text-center sm:bizF-mt-5">
					<h3 className="bizF-text-[2rem] bizF-mb-3 bizF-font-semibold bizF-leading-8 bizF-text-[--bF-theme-h-text]">
						Quote Received
					</h3>
					<div className="bizF-mt-5 bizF-mb-5">
						<p className="bizF-text-md bizF-text-gray-500">
							Thanks for contacting us! We will get in touch with you shortly.
						</p>
					</div>
				</div>
			</div>
		</>
	);
}

function FormVerified({ className }: { className?: string }) {
	return (
		<>
			<div className={clsx(className, "bizF-relative")}>
				<div className="bizF-mx-auto bizF-flex bizF-h-20 bizF-w-20 bizF-items-center bizF-justify-center bizF-rounded-full bizF-bg-[--bF-bullet-bg]">
					<CheckIcon
						className="bizF-h-10 bizF-w-10 bizF-text-[--bF-theme-color]"
						aria-hidden="true"
					/>
				</div>
				<div className="bizF-mt-3 bizF-text-center sm:bizF-mt-5">
					<h3 className="bizF-text-[2rem] bizF-mb-3 bizF-font-semibold bizF-leading-8 bizF-text-[--bF-theme-h-text]">
						Thank you!
					</h3>
					<div className="bizF-mt-5 bizF-mb-5">
						<p className="bizF-text-md bizF-text-gray-500">Successfully verified</p>
					</div>
				</div>
			</div>
		</>
	);
}
